import { DateTime } from "luxon";
import {
  DEFAULT_REQUEST_CRED_SETTINGS,
  IntervalModel,
  SchoolClosure,
  SchoolHoursModel,
  SchoolModel,
} from "model";

import { generateSelectableDaysFromExistingSelectableDays, TimesTableRowType } from "./hooks";

/**
 * Return a continuous list of selectable day date types given a list of non-
 * continuous intervals.
 *
 * This is used to generate every day in the DayField section.
 */
export function generateSelectableDaysFromIntervals(params: {
  now: DateTime;
  intervals: Array<IntervalModel>;
  schoolHours: SchoolHoursModel;
  schoolClosures?: Array<SchoolClosure>;
  shouldUsePreviousWeekdayTimes?: boolean;
}) {
  const { intervals, schoolClosures, shouldUsePreviousWeekdayTimes } = params;

  // Transform the intervals into SelectableDays
  const selectableDays = intervals.map((interval) => {
    const schoolClosureOnThisDay = findSchoolClosureForInterval({
      interval,
      schoolClosures,
    });

    return newSelectableDay({
      interval: {
        start: interval.start,
        end: interval.end,
      },
      isEnabled: !schoolClosureOnThisDay,
      label: schoolClosureOnThisDay?.reason,
    });
  });

  return generateSelectableDaysFromExistingSelectableDays({
    now: params.now,
    startDate: intervals[0].start.startOf("day"),
    endDate: intervals[intervals.length - 1].end.endOf("day"),
    selectableDays: selectableDays,
    schoolHours: params.schoolHours,
    schoolClosures,
    // FIXME: Make this better.
    // This is the only usage of this flag since when we are initially generating
    // the missing days, we want to disable them. But afterwards, we want to
    // enable them since it's a user action.
    disableMissingNonSchoolClosureDays: true,
    shouldUsePreviousWeekdayTimes,
  });
}

/**
 * Given an Interval and School Closures, return either `undefined` or the
 * School Closure which the Interval falls into.
 */
export function findSchoolClosureForInterval(params: {
  interval: { start: DateTime; end: DateTime };
  schoolClosures?: Array<SchoolClosure>;
}) {
  const { interval, schoolClosures } = params;

  // If there are no School Closures, return undefined
  if (!schoolClosures) return undefined;

  // For each School Closure
  for (const schoolClosure of schoolClosures) {
    // If the Interval is before the School Closure `start`
    if (interval.end < DateTime.fromISO(schoolClosure.start)) {
      // Then skip to the next Closure
      continue;
    }

    // If the Interval is after the School Closure `end`
    if (DateTime.fromISO(schoolClosure.end) < interval.start) {
      // Then skip to the next Closure
      continue;
    }

    return schoolClosure;
  }

  return undefined;
}

/**
 * Returns a new SelectableDay type given an interval, and optional label and
 * enabled status.
 */
export function newSelectableDay(params: {
  interval: { start: DateTime; end: DateTime };
  isEnabled?: boolean;
  label?: string;
}): TimesTableRowType {
  const { interval, isEnabled = false, label } = params;

  return {
    // TODO: This can be once we get the code cleaned up.
    dateString: interval.start,
    startTime: interval.start,
    endTime: interval.end,
    isEnabled,
    label,
    areTimesValid: {
      start: undefined,
      end: undefined,
    },
  };
}

export const calculateCredentialRequired = (
  school: SchoolModel,
  formCredentialRequired: boolean,
) => {
  switch (school?.defaultRequestCredentialSetting) {
    case DEFAULT_REQUEST_CRED_SETTINGS.CREDENTIALED:
      return true;
    case DEFAULT_REQUEST_CRED_SETTINGS.NONCREDENTIALED:
      return false;
    case DEFAULT_REQUEST_CRED_SETTINGS.USE_FORM_VALUE:
      return formCredentialRequired;
    // This is redundant - the API should fail before it gets
    // to this point, but no danger in adding the additional
    // check.
    default:
      return formCredentialRequired;
  }
};

function diff(obj1: Record<string, unknown>, obj2: Record<string, unknown>) {
  const keys: string[] = [];

  Object.entries(obj1).map(([key, value]) => {
    // skip if both values are falsy (undefined or null or "")
    if (!value && !obj2[key]) {
      return;
    }

    // if the value exists in the new object but not in the old object,
    // then add the key
    if (typeof obj2[key] === "undefined" && typeof obj1[key] !== "undefined") {
      keys.push(key);
    }

    if (JSON.stringify(value) !== JSON.stringify(obj2[key])) {
      keys.push(key);
      return;
    }
  });

  return keys;
}

/**
 * Returns a list of keys that are different between two objects.
 * @param a - The first object.
 * @param b - The second object.
 * @returns A list of keys that are different between the two objects
 */
export function diffKeys(a: Record<string, unknown>, b: Record<string, unknown>) {
  // if undefined or null, return empty array
  if (a === undefined || b === undefined || a === null || b === null) {
    return [];
  }

  const keys1 = diff(a, b);
  const keys2 = diff(b, a);

  // to remove duplicates
  const set = new Set([...keys1, ...keys2]);

  return Array.from(set);
}

export type SubScheduleDay<T = DateTime> = Readonly<{
  start: T;
  end: T;
  startOffset: number;
  endOffset: number;
}>;
